/**
 * ⚠️ KEEP IN SYNC:
 *
 * This file has a corresponding `report.util.ts` in the other layer
 * (frontend/business-layer). Any changes made here MUST also be applied there.
 *
 * Frontend: report template preview
 * Business layer: report PDF generation, including scheduled reports
 *
 * Both implementations must remain functionally consistent so that
 * previews and generated reports produce the same results.
 */
import type { IClinic } from '@dataTypes/clinic.type'
import type {
  ReportMetricSchema,
  ReportTableColumnSchema,
} from '@services/api/report.api'
import type { ReportPayloadData } from '@services/reports/reportPayloadData'

type ReportDataRow = Record<string, unknown>

type ResolvedReportExtendedBlock = {
  columns: ReportTableColumnSchema[]
  rows: ReportDataRow[]
  headerTitle?: string
  headerSubtitle?: string
}

type ResolvedReportMetric = {
  id: string
  label: string
  columns: ReportTableColumnSchema[]
  rows: ReportDataRow[]
  headerTitle?: string
  headerSubtitle1?: string
  columns2?: ReportTableColumnSchema[]
  rows2?: ReportDataRow[]
  headerTitle2?: string
  headerSubtitle2?: string
  extendedBlocks?: ResolvedReportExtendedBlock[]
}

type BuildReportPayloadArgs = {
  clinicName: string
  clinicLogoUrl?: string
  reportTitle: string
  metricsData?: ResolvedReportMetric[]
  paramLines: string[]
  generatedOn: string
  showAiInsights?: boolean
}
export interface ReportTableRow {
  cells: string[]
  isTotal?: boolean
  emphasize?: boolean
}

export interface ReportSimpleTable {
  type: 'simple'
  subtitle?: string
  columns: string[]
  rows: ReportTableRow[]
}

export interface ReportComparisonTable {
  type: 'comparison'
  subtitle?: string
  labelHeader: string
  groups: string[]
  periodLeft: string
  periodRight: string
  wide?: boolean
  rows: ReportTableRow[]
}

export type ReportTable = ReportSimpleTable | ReportComparisonTable

export interface ReportBlock {
  title: string
  paramLine?: string
  fullWidth?: boolean
  scrollable?: boolean
  tables: ReportTable[]
}

export interface ReportTableGroup {
  modifier?: string // '', 'treatment-funnel-preview', 'pbh-comparison-stacked'
  blocks: ReportBlock[]
}

export interface ReportPayload {
  isEmpty?: boolean
  title: string
  generatedOn: string
  clinicName: string
  clinicLogoUrl?: string
  paramLines: string[]
  tableGroups: ReportTableGroup[]
  showAiInsights?: boolean
  aiInsightsHtml?: string
}

/* eslint-disable indent */
export function buildReportPayload(
  args: BuildReportPayloadArgs,
): ReportPayload {
  const {
    clinicName,
    clinicLogoUrl,
    reportTitle,
    metricsData,
    paramLines,
    generatedOn,
    showAiInsights,
  } = args

  return {
    title: reportTitle,
    generatedOn,
    clinicName,
    clinicLogoUrl,
    paramLines,
    showAiInsights,
    tableGroups: [
      ...(metricsData
        ? metricsData.flatMap((metric) => [
            {
              blocks: [
                {
                  title: metric.headerTitle || metric.label,
                  tables: [
                    {
                      type: 'simple' as const,
                      ...(metric.headerSubtitle1
                        ? { subtitle: metric.headerSubtitle1 }
                        : {}),
                      columns: metric.columns.map((column) => column.label),
                      rows: metric.rows.map((row) => ({
                        cells: metric.columns.map((column) =>
                          String(row[column.id] ?? ''),
                        ),
                      })),
                    },
                  ],
                },
                ...(metric.columns2
                  ? [
                      {
                        title: metric.headerTitle2 || '',
                        tables: [
                          {
                            type: 'simple' as const,
                            ...(metric.headerSubtitle2
                              ? { subtitle: metric.headerSubtitle2 }
                              : {}),
                            columns: metric.columns2.map(
                              (column) => column.label,
                            ),
                            rows: (metric.rows2 ?? []).map((row) => ({
                              cells:
                                metric.columns2?.map((column) =>
                                  String(row[column.id] ?? ''),
                                ) ?? [],
                            })),
                          },
                        ],
                      },
                    ]
                  : []),
              ],
            },

            ...(metric.extendedBlocks?.map((block) => ({
              blocks: [
                {
                  title: block.headerTitle || '',
                  tables: [
                    {
                      type: 'simple' as const,
                      ...(block.headerSubtitle
                        ? { subtitle: block.headerSubtitle }
                        : {}),
                      columns: block.columns.map((column) => column.label),
                      rows: block.rows.map((row) => ({
                        cells: block.columns.map((column) =>
                          String(row[column.id] ?? ''),
                        ),
                      })),
                    },
                  ],
                },
              ],
            })) ?? []),
          ])
        : []),
      // ...remaining sections
    ],
  }
}

export const getReportMetricById = (
  sourceMetricsSchema: ReportMetricSchema[],
  metricId: string,
  isCustomReport: boolean = false,
  data?: ReportDataRow[],
  data2?: ReportDataRow[],
  extendedData?: ReportDataRow[][],
): ResolvedReportMetric | undefined => {
  const metricObj = sourceMetricsSchema.find((metric) => metric.id === metricId)
  return metricObj
    ? {
        id: metricObj.id,
        label: isCustomReport
          ? metricObj.customLabel || metricObj.label
          : metricObj.label,
        columns: metricObj.columns || [],
        ...(data ? { rows: data } : { rows: [] }),
        ...(metricObj.headerTitle
          ? { headerTitle: metricObj.headerTitle }
          : {}),
        ...(metricObj.headerSubtitle1
          ? { headerSubtitle1: metricObj.headerSubtitle1 }
          : {}),
        ...(metricObj.columns2
          ? {
              columns2: metricObj.columns2,
              ...(data2 ? { rows2: data2 } : { rows2: [] }),
              ...(metricObj.headerTitle2
                ? { headerTitle2: metricObj.headerTitle2 }
                : {}),
            }
          : {}),
        ...(metricObj.extendedBlocks
          ? {
              extendedBlocks: metricObj.extendedBlocks.map(
                (block, index: number) => ({
                  columns: block.columns,
                  rows: extendedData?.[index] ?? block.sample ?? [],
                  ...(block.headerTitle
                    ? { headerTitle: block.headerTitle }
                    : {}),
                }),
              ),
            }
          : {}),
      }
    : undefined
}

function formatValueText(text: string): string {
  const lower = text.replace(/-/g, ' ').toLowerCase()
  const idx = lower.search(/[a-z]/)

  if (idx === -1) return lower

  return (
    lower.slice(0, idx) + lower.charAt(idx).toUpperCase() + lower.slice(idx + 1)
  )
}

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    timeZone: 'UTC',
  })
}

function formatQuarter(value: string): string {
  const quarters: Record<string, string> = {
    'calendar-q1': 'Q1 (Jan–Mar)',
    'calendar-q2': 'Q2 (Apr–Jun)',
    'calendar-q3': 'Q3 (Jul–Sep)',
    'calendar-q4': 'Q4 (Oct–Dec)',
  }

  return quarters[value] ?? value
}

function buildParamLines(
  selection: Record<string, string>,
  providerName?: string,
): string[] {
  if (!selection) return []

  let reportPeriod = selection['period-preset']
    ? formatValueText(formatQuarter(selection['period-preset']))
    : ''

  const dataMode = selection['period-category']
    ? formatValueText(selection['period-category'])
    : ''

  let provider = selection['provider-filter']
    ? formatValueText(selection['provider-filter'])
    : ''

  if (selection['period-preset'] === 'date-range') {
    reportPeriod = `${formatDate(selection['hist-range-start'] ?? '')} - ${formatDate(selection['hist-range-end'] ?? '')}`
  } else if (
    selection['period-category'] === 'snapshot' &&
    selection['period-preset'] === 'snapshot'
  ) {
    reportPeriod = `${formatDate(selection['snapshot-as-of'] ?? '')} ${selection['snapshot-time'] ?? ''}`
  }

  if (selection['provider-filter'] !== 'all-providers') {
    if (providerName) {
      provider = providerName
    }
  }

  return [
    `Report period: ${reportPeriod}`,
    `Data mode: ${dataMode}`,
    `Provider: ${provider}`,
  ]
}

export function buildReportPreviewPayload({
  selectedClinic,
  reportTitle,
  reportFiltersAndMetrics,
  generatedOnDisplay,
  headerSwitch,
  isCustomReport,
  sourceMetricsSchema,
  payloadData,
  providerName,
  clinicLogoUrl,
}: {
  selectedClinic: Pick<IClinic, 'clinic_name'> | null
  reportTitle: string
  reportFiltersAndMetrics: {
    filters: Record<string, string>
    metrics: Record<string, boolean>
  } | null
  generatedOnDisplay: string
  headerSwitch: boolean
  isCustomReport: boolean
  sourceMetricsSchema: ReportMetricSchema[]
  payloadData?: ReportPayloadData
  providerName?: string
  clinicLogoUrl?: string
}): ReportPayload {
  const metricsOrder = isCustomReport
    ? sourceMetricsSchema
        .filter((metric) => metric.customCategory)
        .map((metric) => metric.id)
    : sourceMetricsSchema.map((metric) => metric.id)

  const metricsData =
    metricsOrder
      .filter((key: string) => reportFiltersAndMetrics?.metrics?.[key])
      .map((key: string) =>
        getReportMetricById(
          sourceMetricsSchema,
          key,
          isCustomReport,
          payloadData?.[key]?.data,
          payloadData?.[key]?.data2,
          payloadData?.[key]?.extendedData,
        ),
      )
      .filter(
        (metric): metric is ResolvedReportMetric => metric !== undefined,
      ) ?? []

  return buildReportPayload({
    clinicName: selectedClinic?.clinic_name ?? '',
    clinicLogoUrl,
    reportTitle: reportTitle,
    metricsData,
    paramLines: buildParamLines(
      reportFiltersAndMetrics?.filters as Record<string, string>,
      providerName,
    ),
    generatedOn: generatedOnDisplay,
    showAiInsights: headerSwitch,
  })
}
